/* =========================
   PEGUU — PURCHASE FLOW
   CONTROL GLOBAL DEL PROCESO
========================= */

(() => {
  "use strict";


  /* =========================
     CONFIGURACIÓN
  ========================= */

  const MATERIAL_STORAGE_KEY =
    "peguu:selected-material";

  const QR_CONFIG_STORAGE_KEY =
    "peguu:qr-config";

  const ORDER_STORAGE_KEY =
    "peguu:sticker-order";

  const DESIGN_STORAGE_KEY =
    "peguu:sticker-design";

  const PAYMENT_STORAGE_KEY =
    "peguu:sticker-payment";

  const BILLING_STORAGE_KEY =
    "peguu:billing-data";

  const PENDING_ORDER_STORAGE_KEY =
    "peguu:pending-order-id";

  const CONFIRMATION_STORAGE_KEY =
    "peguu:purchase-confirmation";

  const GUEST_CART_STORAGE_KEY =
    "peguu:guest-cart";

  const GUEST_COUPON_STORAGE_KEY =
    "peguu:guest-coupon";

  const CUSTOMER_CART_METADATA_KEY =
    "peguu:customer-cart-metadata";

  const CART_EDIT_STORAGE_KEY =
    "peguu:cart-edit";

  const DESIGN_FILE_METADATA_KEY =
    "peguu:design-file-metadata";

  const DESIGN_FILE_DB =
    "peguu-design-files";

  const DESIGN_FILE_STORE =
    "files";

  const QR_STORAGE_KEYS = [
    QR_CONFIG_STORAGE_KEY,
    "peguu:sticker-qr",
    "peguu:qr-configuration",
    "peguu:qr-url",
    "peguu:qr-position",
    "peguu:qr-finish"
  ];

  const PAYMENT_STORAGE_KEYS = [
    PAYMENT_STORAGE_KEY,
    "peguu:checkout-contact",
    "peguu:shipping-data",
    "peguu:payment-data"
  ];


  const QR_CONFIG_PATH =
    "/pages/configura-qr";

  const MATERIALS_PATH =
    "/pages/materiales";

  const MEASUREMENTS_PATH =
    "/pages/arma-tus-stickers";

  const DESIGN_PATH =
    "/pages/arma-tus-stickers-diseno";

  const SUMMARY_PATH =
    "/pages/arma-tus-stickers-resumen";

  const PAYMENT_PATHS = [
    "/pages/arma-tus-stickers-pago",
    "/pages/revisa-y-paga"
  ];


  /* =========================
     RUTAS COMUNES DEL PROCESO
  ========================= */

  /*
   * Estas rutas pertenecen tanto
   * al flujo normal como al flujo QR.
   *
   * FLUJO NORMAL:
   *
   * Materiales
   * ↓
   * Medidas
   * ↓
   * Diseño
   * ↓
   * Resumen
   *
   *
   * FLUJO QR:
   *
   * Materiales
   * ↓
   * Configura QR
   * ↓
   * Medidas
   * ↓
   * Diseño
   * ↓
   * Resumen
   */

  const COMMON_FLOW_PATHS = [
    "/pages/materiales",
    "/pages/arma-tus-stickers",
    "/pages/arma-tus-stickers-diseno",
    "/pages/arma-tus-stickers-resumen"
  ];


  /* =========================
     DATOS COMPLETOS DEL PROCESO
  ========================= */

  /*
   * Mientras el usuario permanezca
   * dentro del proceso, estos datos
   * se conservan.
   *
   * Al abandonar el proceso,
   * se eliminan todos.
   */

  const FLOW_STORAGE_KEYS = [
    MATERIAL_STORAGE_KEY,
    ORDER_STORAGE_KEY,
    DESIGN_STORAGE_KEY,
    BILLING_STORAGE_KEY,
    CONFIRMATION_STORAGE_KEY,
    PENDING_ORDER_STORAGE_KEY,
    CART_EDIT_STORAGE_KEY,
    ...QR_STORAGE_KEYS,
    ...PAYMENT_STORAGE_KEYS
  ];


  /* =========================
     NORMALIZAR RUTA
  ========================= */

  const normalizePath = (
    pathname
  ) => {

    return (
      String(
        pathname || ""
      )
        .replace(
          /\/+$/,
          ""
        ) ||
      "/"
    );
  };


  const isCheckoutNavigation = (
    url
  ) => {
    const pathname =
      normalizePath(url?.pathname);

    return (
      PAYMENT_PATHS.includes(pathname) ||
      /^\/checkouts?(?:\/|$)/i.test(pathname)
    );
  };


  /* =========================
     LEER MATERIAL SELECCIONADO
  ========================= */

  const getSelectedMaterial =
    () => {

      try {

        const stored =
          sessionStorage.getItem(
            MATERIAL_STORAGE_KEY
          );


        if (
          !stored
        ) {
          return null;
        }


        const material =
          JSON.parse(
            stored
          );


        if (
          !material ||
          !material.id
        ) {
          return null;
        }


        return material;


      } catch (
        error
      ) {

        console.warn(
          "Peguu Flow: no fue posible leer el material seleccionado.",
          error
        );


        return null;
      }
    };


  /* =========================
     LEER ESTADO DEL PROCESO
  ========================= */

  const getStoredState = (
    key
  ) => {
    try {
      const stored =
        sessionStorage.getItem(
          key
        );

      return stored
        ? JSON.parse(stored)
        : null;
    } catch (error) {
      console.warn(
        `Peguu Flow: no fue posible leer ${key}.`,
        error
      );

      return null;
    }
  };


  const hasValidOrder = () => {
    const order =
      getStoredState(
        ORDER_STORAGE_KEY
      );

    const widthCm =
      Number(order?.measurements?.widthCm);

    const heightCm =
      Number(order?.measurements?.heightCm);

    return Boolean(
      order?.material?.id &&
      Number.isInteger(widthCm) &&
      widthCm >= 3 &&
      widthCm <= 80 &&
      Number.isInteger(heightCm) &&
      heightCm >= 3 &&
      heightCm <= 80 &&
      Number(order?.quote?.quantity) > 0
    );
  };


  const hasValidDesign = () => {
    const design =
      getStoredState(
        DESIGN_STORAGE_KEY
      );

    return Boolean(
      design?.uploadLater === true ||
      design?.primaryFile?.fileName
    );
  };


  const hasValidQrConfig = () => {
    const config =
      getStoredState(
        QR_CONFIG_STORAGE_KEY
      );

    return Boolean(
      config?.materialId &&
      config?.destinationUrl &&
      config?.acabado &&
      config?.posicion
    );
  };


  /* =========================
     VALIDAR FLUJO QR
  ========================= */

  const isQrFlow =
    () => {

      const material =
        getSelectedMaterial();


      const productCode =
        String(
          material?.productCode ||
          ""
        )
          .trim()
          .toLowerCase();


      return (
        productCode ===
        "qr_sticker"
      );
    };


  /* =========================
     PROTEGER PASOS DEL FLUJO
  ========================= */

  const enforceCurrentStep = () => {
    const currentPath =
      normalizePath(
        window.location.pathname
      );

    const isCartCheckoutEntry =
      currentPath ===
        normalizePath(SUMMARY_PATH) &&
      new URLSearchParams(
        window.location.search
      ).has("open-payment");

    /*
     * Drawer y carrito completo pueden entrar al resumen sin una
     * configuración individual activa: el carrito es la fuente de verdad.
     */
    if (isCartCheckoutEntry) {
      return false;
    }

    const material =
      getSelectedMaterial();

    let redirectPath = "";

    if (
      currentPath ===
        normalizePath(QR_CONFIG_PATH) &&
      !isQrFlow()
    ) {
      redirectPath =
        MATERIALS_PATH;
    } else if (
      currentPath ===
        normalizePath(MEASUREMENTS_PATH) &&
      !material
    ) {
      redirectPath =
        MATERIALS_PATH;
    } else if (
      currentPath ===
        normalizePath(MEASUREMENTS_PATH) &&
      isQrFlow() &&
      !hasValidQrConfig()
    ) {
      redirectPath =
        QR_CONFIG_PATH;
    } else if (
      currentPath ===
        normalizePath(DESIGN_PATH) &&
      !material
    ) {
      redirectPath =
        MATERIALS_PATH;
    } else if (
      currentPath ===
        normalizePath(DESIGN_PATH) &&
      !hasValidOrder()
    ) {
      redirectPath =
        MEASUREMENTS_PATH;
    } else if (
      currentPath ===
        normalizePath(SUMMARY_PATH) &&
      !material
    ) {
      redirectPath =
        MATERIALS_PATH;
    } else if (
      currentPath ===
        normalizePath(SUMMARY_PATH) &&
      !hasValidOrder()
    ) {
      redirectPath =
        MEASUREMENTS_PATH;
    } else if (
      currentPath ===
        normalizePath(SUMMARY_PATH) &&
      !hasValidDesign()
    ) {
      redirectPath =
        DESIGN_PATH;
    }

    if (!redirectPath) {
      return false;
    }

    window.location.replace(
      redirectPath
    );

    return true;
  };


  /* =========================
     VALIDAR RUTA DEL PROCESO
  ========================= */

  const isFlowPath = (
    pathname
  ) => {

    const normalizedPath =
      normalizePath(
        pathname
      );


    /* =========================
       RUTAS COMUNES
    ========================= */

    const isCommonPath =
      COMMON_FLOW_PATHS.some(
        (
          path
        ) => {

          return (
            normalizePath(
              path
            ) ===
            normalizedPath
          );
        }
      );


    if (
      isCommonPath
    ) {
      return true;
    }


    /* =========================
       CONFIGURA TU QR
    ========================= */

    /*
     * Esta página solamente forma
     * parte del proceso cuando el
     * producto seleccionado es:
     *
     * productCode = qr_sticker
     */

    if (
      normalizedPath ===
      normalizePath(
        QR_CONFIG_PATH
      )
    ) {

      return isQrFlow();
    }


    return false;
  };


  /* =========================
     LIMPIAR PROCESO COMPLETO
  ========================= */

  const clearPurchaseFlow =
    () => {

      FLOW_STORAGE_KEYS.forEach(
        (
          key
        ) => {

          try {

            sessionStorage.removeItem(
              key
            );


          } catch (
            error
          ) {

            console.error(
              `No fue posible limpiar ${key}:`,
              error
            );
          }
        }
      );
    };


  const clearLocalDesignFiles =
    () => {

      if (!window.indexedDB) {
        return Promise.resolve();
      }

      return new Promise(
        (resolve, reject) => {
          const request =
            indexedDB.open(
              DESIGN_FILE_DB,
              1
            );

          request.onerror =
            () => reject(
              request.error
            );

          request.onsuccess =
            () => {
              const database =
                request.result;

              if (
                !database.objectStoreNames
                  .contains(
                    DESIGN_FILE_STORE
                  )
              ) {
                database.close();
                resolve();
                return;
              }

              const transaction =
                database.transaction(
                  DESIGN_FILE_STORE,
                  "readwrite"
                );

              transaction
                .objectStore(
                  DESIGN_FILE_STORE
                )
                .clear();

              transaction.oncomplete =
                () => {
                  database.close();
                  resolve();
                };

              transaction.onerror =
                () => {
                  database.close();
                  reject(
                    transaction.error
                  );
                };
            };
        }
      );
    };


  const getPendingOrderId =
    () =>
      String(
        sessionStorage.getItem(
          PENDING_ORDER_STORAGE_KEY
        ) ||
        localStorage.getItem(
          PENDING_ORDER_STORAGE_KEY
        ) ||
        ""
      ).trim();


  const clearPendingOrderId = (
    orderId
  ) => {
    if (
      getPendingOrderId() !==
        String(orderId || "").trim()
    ) {
      return;
    }

    [
      sessionStorage,
      localStorage
    ].forEach((storage) => {
      try {
        storage.removeItem(
          PENDING_ORDER_STORAGE_KEY
        );
      } catch (error) {
        console.warn(
          "Peguu Flow: no fue posible limpiar el pedido pendiente obsoleto.",
          error
        );
      }
    });
  };


  const getPendingPaymentStatus =
    async (orderId) => {

      if (
        typeof window.PeguuCheckout
          ?.getPaymentStatus ===
        "function"
      ) {
        return window.PeguuCheckout
          .getPaymentStatus(orderId);
      }

      const apiBaseUrl =
        window.PeguuApi
          ?.endpoint?.(
            "customerOrders"
          );

      if (!apiBaseUrl) {
        throw new Error(
          "No fue posible consultar el estado del pago."
        );
      }

      const paymentStatusUrl =
        `${apiBaseUrl}/${encodeURIComponent(orderId)}/payment-status`;

      let response =
        await window.PeguuAuthClient.authFetch(
          paymentStatusUrl,
          {
            headers: {
              Accept:
                "application/json"
            }
          }
        );

      let payload =
        await response.json();

      /*
       * Un cliente con sesión puede haber comprado como invitado (por
       * ejemplo, mientras su correo aún no está verificado). En ese caso
       * el token no tiene acceso al pedido y payment-status debe consultarse
       * con el mismo contexto público con el que se creó el checkout.
       */
      if (response.status === 403) {
        response =
          await window.fetch(
            paymentStatusUrl,
            {
              headers: {
                Accept:
                  "application/json"
              }
            }
          );

        payload =
          await response.json();
      }

      if (!response.ok) {
        const apiError = new Error(
          payload?.message ||
          "No fue posible consultar el estado del pago."
        );

        apiError.status =
          response.status;

        throw apiError;
      }

      const data =
        payload?.data ||
        payload;

      return {
        ...data,
        status:
          String(
            data?.status || ""
          ).trim().toLowerCase(),
        paymentStatus:
          String(
            data?.paymentStatus || ""
          ).trim().toLowerCase()
      };
    };


  let pendingPaymentReconciliation =
    null;

  let pendingPaymentRetryTimer =
    null;

  let pendingPaymentRetryCount =
    0;

  const PENDING_PAYMENT_RETRY_LIMIT =
    10;

  const PENDING_PAYMENT_RETRY_DELAY =
    2500;


  const reconcilePendingPayment =
    async () => {

      if (pendingPaymentReconciliation) {
        return pendingPaymentReconciliation;
      }

      const orderId =
        getPendingOrderId();

      if (!orderId) {
        return null;
      }

      pendingPaymentReconciliation =
        (async () => {
          const payment =
            await getPendingPaymentStatus(
              orderId
            );

          const isPaid =
            payment?.status === "paid" ||
            payment?.paymentStatus === "paid";

          if (!isPaid) {
            return payment;
          }

          if (
            typeof window.PeguuCart
              ?.clearPaidCart !==
            "function"
          ) {
            throw new Error(
              "No fue posible limpiar el carrito pagado."
            );
          }

          await window.PeguuCart
            .clearPaidCart();

          await clearLocalDesignFiles();

          [
            GUEST_CART_STORAGE_KEY,
            GUEST_COUPON_STORAGE_KEY,
            CUSTOMER_CART_METADATA_KEY,
            DESIGN_FILE_METADATA_KEY,
            PENDING_ORDER_STORAGE_KEY
          ].forEach((key) => {
            localStorage.removeItem(key);
          });

          clearPurchaseFlow();

          return payment;
        })()
          .catch((error) => {
            const status =
              Number(error?.status) || 0;
            const isInaccessibleOrder =
              (
                status === 403 ||
                status === 404
              ) &&
              /no tienes acceso/i.test(
                String(
                  error?.message || ""
                )
              );

            if (isInaccessibleOrder) {
              clearPendingOrderId(
                orderId
              );

              return null;
            }

            console.warn(
              "Peguu Flow: no fue posible reconciliar el pago pendiente.",
              error
            );

            return null;
          })
          .finally(() => {
            pendingPaymentReconciliation =
              null;
          });

      return pendingPaymentReconciliation;
    };


  const schedulePendingPaymentRetry =
    () => {

      const orderId =
        getPendingOrderId();

      if (
        !orderId ||
        pendingPaymentRetryCount >=
          PENDING_PAYMENT_RETRY_LIMIT
      ) {
        return;
      }

      window.clearTimeout(
        pendingPaymentRetryTimer
      );

      pendingPaymentRetryCount += 1;

      pendingPaymentRetryTimer =
        window.setTimeout(
          async () => {
            pendingPaymentRetryTimer =
              null;

            await reconcilePendingPayment();

            if (
              getPendingOrderId()
            ) {
              schedulePendingPaymentRetry();
            }
          },
          PENDING_PAYMENT_RETRY_DELAY
        );
    };


  const startPendingPaymentReconciliation =
    async () => {

      pendingPaymentRetryCount = 0;

      window.clearTimeout(
        pendingPaymentRetryTimer
      );

      pendingPaymentRetryTimer =
        null;

      await reconcilePendingPayment();

      if (
        getPendingOrderId()
      ) {
        schedulePendingPaymentRetry();
      }
    };


  /* =========================
     LIMPIAR QR NO UTILIZADO
  ========================= */

  const clearUnusedQrConfig =
    () => {

      /*
       * La configuración QR solamente
       * debe existir cuando el producto
       * seleccionado es qr_sticker.
       */

      const material =
        getSelectedMaterial();


      /*
       * Si todavía no existe un
       * material seleccionado,
       * no hacemos nada.
       */

      if (
        !material
      ) {
        return;
      }


      /*
       * Si estamos comprando QR,
       * conservar configuración.
       */

      if (
        isQrFlow()
      ) {
        return;
      }


      /*
       * Material normal:
       *
       * eliminar cualquier configuración
       * QR sobrante de una compra anterior.
       */

      try {

        sessionStorage.removeItem(
          QR_CONFIG_STORAGE_KEY
        );


      } catch (
        error
      ) {

        console.warn(
          "Peguu Flow: no fue posible limpiar la configuración QR.",
          error
        );
      }
    };


  /* =========================
     DETECTAR SALIDA DEL FLUJO
  ========================= */

  document.addEventListener(
    "click",
    (
      event
    ) => {

      const link =
        event.target.closest(
          "a[href]"
        );


      if (
        !link
      ) {
        return;
      }

      /*
       * Abrir el drawer no abandona el configurador. El control del
       * encabezado conserva un href a /cart como respaldo, pero el drawer
       * intercepta el clic; no debemos limpiar el pedido en curso.
       */
      if (
        link.hasAttribute(
          "data-peguu-cart-open"
        )
      ) {
        return;
      }


      const href =
        link.getAttribute(
          "href"
        );


      /* =========================
         IGNORAR ENLACES ESPECIALES
      ========================= */

      if (
        !href ||
        href.startsWith(
          "#"
        ) ||
        href.startsWith(
          "mailto:"
        ) ||
        href.startsWith(
          "tel:"
        ) ||
        href.startsWith(
          "javascript:"
        )
      ) {
        return;
      }


      let url;


      try {

        url =
          new URL(
            link.href,
            window.location.origin
          );


      } catch {

        return;
      }


      /* =========================
         NAVEGACIÓN INTERNA
         DEL PROCESO
      ========================= */

      /*
       * Si el usuario continúa dentro
       * del proceso de compra,
       * NO borrar ningún dato.
       *
       * Se conservan:
       *
       * - material
       * - configuración QR
       * - medidas / cantidad
       * - diseño
       */

      if (
        url.origin ===
          window.location.origin &&
        isFlowPath(
          url.pathname
        )
      ) {

        return;
      }


      /*
       * Shopify Checkout necesita conservar el estado temporal para
       * poder reconciliar el pago al regresar.
       */
      if (isCheckoutNavigation(url)) {
        return;
      }

      clearPurchaseFlow();
    }
  );


  /* =========================
     ESTADO INICIAL
  ========================= */

  /*
   * Si actualmente existe un material
   * normal seleccionado, nos aseguramos
   * de que no quede una configuración QR
   * perteneciente a una compra anterior.
   */

  const currentPath =
    normalizePath(
      window.location.pathname
    );

  if (
    !isFlowPath(currentPath) &&
    !isCheckoutNavigation(
      new URL(
        window.location.href
      )
    )
  ) {
    clearPurchaseFlow();
  }

  if (!enforceCurrentStep()) {
    clearUnusedQrConfig();
  }


  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        void startPendingPaymentReconciliation();
      },
      { once: true }
    );
  } else {
    void startPendingPaymentReconciliation();
  }


  window.addEventListener(
    "pageshow",
    () => {
      void startPendingPaymentReconciliation();
    }
  );

})();
